import { Calendar } from "@fullcalendar/core";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";

const Hooks = {};

Hooks.calendar = {
  mounted() {
    let loadedRanges = {};

    /**
     * This method is used to asynchronously load events; it's invoked as part of the
     * `eventSources` calendar option. Since we can't load the data synchronously, we need
     * to pass an empty array to the `success` callback.
     * */
    const loadEvents = () => (data, success) => {
      /**
       * Since there's no control over the range of events that are loaded, we have a guarantee
       * that we can use to build a dictionary to assist in preventing loading events that have
       * already been loaded for the given period. This would prevent new events from being loaded
       * if an event had been added to the calendar since page load. A full page refresh would be
       * necessary to load any new events. That's a tradeoff for incessantly loading events on every
       * calendar view change.
       * */
      if (
        Object.prototype.hasOwnProperty.call(loadedRanges, data.start) === true
      ) {
        success([]);
      } else {
        loadedRanges = {
          ...loadedRanges,
          [data.start]: data.end,
        };

        this.pushEventTo(this.el, "events:load", { calendar: data });

        success([]);
      }
    };

    this.calendar = new Calendar(this.el, {
      loading: (loading) => {
        if (loading === false) {
          const loadingIndicator = document.getElementById(
            `${this.el.id}-loading-indicator`,
          );

          if (!loadingIndicator) return;

          loadingIndicator.classList.add("tw:hidden");
          this.el.classList.remove("tw:hidden");
        }
      },
      height: 800,
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "customMonth,monthList",
      },
      plugins: [
        dayGridPlugin,
        interactionPlugin,
        listPlugin,
        momentTimezonePlugin,
      ],
      initialView: "customMonth",
      views: {
        customMonth: {
          type: "dayGridMonth",
          buttonText: "Month",
          dayMaxEventRows: true,
        },
        monthList: {
          type: "listWeek",
          duration: { months: 1 },
          buttonText: "List",
        },
      },
      buttonText: {
        today: "Today",
      },
      dateClick: (info) => {
        this.pushEventTo(this.el, "calendar:dateclick", {
          selectedDate: info.dateStr,
          view: this.calendar.view,
        });
        this.showModal();
      },
      datesSet: () => {
        this.pushEventTo(this.el, "calendar:datechange", {
          selectedDate: this.calendar.getDate(),
          view: this.calendar.view,
        });
      },
      eventClick: (info) => {
        this.pushEventTo(this.el, "event:show", { event_id: info.event.id });
        this.showModal("show-event-modal");
      },
      eventClassNames: (arg) => {
        const currentDate = new Date();

        if (arg.event.allDay === true) {
          currentDate.setHours(0, 0, 0, 0);
        }

        const classNames = [];

        if (arg.event.start > currentDate) {
          return classNames;
        }
        if (!!arg.event.end === false && arg.event.start < currentDate) {
          return ["tw:opacity-50"];
        }
        if (!!arg.event.end === true && arg.event.end < currentDate) {
          return ["tw:opacity-50"];
        }
        return classNames;
      },
      eventSources: [loadEvents(this)],
    });

    const { componentId } = this.el.dataset;
    const timezoneSelector = document.getElementById(
      `${componentId}-time-zone-selector`,
    );

    if (timezoneSelector) {
      timezoneSelector.addEventListener("change", (e) => {
        this.calendar.setOption("timeZone", e.target.value);
      });
    }

    this.handleEvent("events:add", ({ events }) => {
      const currentEvents = this.calendar.getEvents().map((e) => e.id);

      events
        .filter((e) => !currentEvents.includes(e.id))
        .map((e) => this.calendar.addEvent(e));
    });

    this.handleEvent("event:add", ({ event }) => {
      this.calendar.addEvent(event);

      const elId = `${componentId}-modal`;
      const el = document.getElementById(elId);

      window.liveSocket.execJS(el, el.getAttribute("phx-remove"));
    });

    this.handleEvent("event:update", ({ event }) => {
      const loadedEvent = this.calendar.getEventById(event.id);

      if (loadedEvent) loadedEvent.remove(event);

      this.calendar.addEvent(event);

      const elId = `${componentId}-modal`;
      const el = document.getElementById(elId);

      window.liveSocket.execJS(el, el.getAttribute("phx-remove"));
    });

    this.handleEvent("event:remove", ({ event }) => {
      const loadedEvent = this.calendar.getEventById(event.id);
      if (loadedEvent) loadedEvent.remove();
    });

    this.calendar.render();
  },

  showCalendar() {
    const { componentId } = this.el.dataset;
    const componentEl = document.getElementById(componentId);

    if (componentEl && componentEl.classList.contains("tw:hidden")) {
      componentEl.classList.remove("tw:hidden");
    }

    this.calendar.render();
  },

  showModal(modalId = "modal") {
    const { componentId } = this.el.dataset;
    const elId = `${componentId}-${modalId}`;
    const el = document.getElementById(elId);

    window.liveSocket.execJS(el, el.getAttribute("data-show"));
  },
};

export default Hooks;
